import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { fetchPersonas } from "../lib/api";
import { ArrowRight, ShieldCheck, Sparkles, Brain, Network, Target, BookOpen, GitBranch, Users, Star } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import GuidedTour, { TourTrigger } from "../components/GuidedTour";
import { getActiveTenant, fetchTenantPersonas } from "../lib/tenantApi";

const ROLE_META = {
  employee: { eyebrow: "Çalışan", color: "from-sky-500/10 to-sky-500/0", border: "hover:border-sky-500/50", accent: "text-sky-600" },
  manager: { eyebrow: "Yönetici", color: "from-amber-500/10 to-amber-500/0", border: "hover:border-amber-500/50", accent: "text-amber-600" },
  hr: { eyebrow: "İK", color: "from-emerald-500/10 to-emerald-500/0", border: "hover:border-emerald-500/50", accent: "text-emerald-700" },
};

const FEATURES = [
  { icon: Brain, label: "Skill Intelligence" },
  { icon: Target, label: "Kariyer Yolu" },
  { icon: Network, label: "Mentor Eşleştirme" },
  { icon: ShieldCheck, label: "Yedekleme & Risk" },
];

export default function RoleSelector() {
  const [personas, setPersonas] = useState([]);
  const [tenantPersonas, setTenantPersonas] = useState([]);
  const nav = useNavigate();
  const tenant = getActiveTenant();
  const isSectorTenant = !!tenant?.industry; // perakende vb. sektörel demo'larda industry alanı dolu

  useEffect(() => {
    if (isSectorTenant) {
      fetchTenantPersonas(tenant.slug).then(setTenantPersonas).catch(() => setTenantPersonas([]));
    } else {
      fetchPersonas().then(setPersonas).catch(() => setPersonas([]));
    }
  }, [isSectorTenant, tenant?.slug]);

  const selectTenantPersona = (p) => {
    sessionStorage.setItem("plena_persona", JSON.stringify(p));
    // Use the same banking dashboards — they're now tenant-aware via X-Tenant-Slug header.
    if (p.role === "hr") nav("/hr");
    else if (p.role === "manager") nav("/manager");
    else nav("/employee");
  };

  const select = (p) => {
    sessionStorage.setItem("plena_persona", JSON.stringify(p));
    nav(`/${p.role}`);
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-50" />
      <div className="absolute -top-40 -left-40 w-[480px] h-[480px] bg-sky-500/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -right-40 w-[480px] h-[480px] bg-amber-500/10 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-8 py-12 lg:py-20">
        {/* Brand */}
        <div className="flex items-center justify-between mb-16">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-sky-700 flex items-center justify-center shadow-lg shadow-sky-500/40">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <div>
              <div className="font-display text-xl font-semibold leading-none">Plena <span className="text-sky-600">Growth</span></div>
              <div className="text-[0.7rem] text-slate-500 mt-0.5 uppercase tracking-[0.2em]">Talent Intelligence</div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <TourTrigger scope="selector" label="Demo Turu Başlat" />
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 border border-slate-200 text-xs text-slate-600">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Plena AI · Türkçe
            </div>
          </div>
        </div>

        {/* Hero */}
        <div className="max-w-3xl mb-16">
          <div className="label-eyebrow text-sky-600 mb-4">YAPAY ZEKA DESTEKLİ YETENEK ZEKASI</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] mb-6">
            Yetenek kararlarını <br />
            <span className="text-slate-600">kara kutudan çıkarın.</span>{" "}
            <span className="bg-gradient-to-r from-sky-600 to-amber-600 bg-clip-text text-transparent">Kanıta dönüştürün.</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
            Plena Growth, çalışan yetkinliklerinden kariyer yollarına, mentorluk uyumundan kritik rol risklerine kadar
            tüm yetenek kararlarını <span className="text-slate-800">açıklanabilir</span> ve <span className="text-slate-800">ölçülebilir</span> kılar.
          </p>

          <div className="flex flex-wrap gap-2 mt-7">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.label} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 border border-slate-200 text-xs text-slate-700">
                  <Icon className="w-3.5 h-3.5 text-sky-600" strokeWidth={1.7} />
                  {f.label}
                </div>
              );
            })}
          </div>
        </div>

        {/* Persona cards (hidden when sector tenant active to avoid showing banking demo on retail tenant) */}
        {!isSectorTenant && (
          <>
            <div className="mb-6">
              <div className="label-eyebrow mb-3">Demo Personayı seçin</div>
              <div className="text-sm text-slate-500 mb-6">Aynı platform, üç farklı bakış. Bir personaya tıklayarak ilgili rol görünümünü açabilirsiniz.</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {personas.map((p, idx) => {
                const meta = ROLE_META[p.role] || ROLE_META.employee;
                return (
                  <button
                    key={p.id}
                    data-testid={`persona-card-${p.role}`}
                    data-tour={`persona-${p.role}-card`}
                    id={`persona-${p.role}-card`}
                    onClick={() => select(p)}
                    style={{ animationDelay: `${idx * 80}ms` }}
                    className={`fade-up group text-left relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 transition-all hover:-translate-y-1 hover:shadow-lg ${meta.border}`}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${meta.color} opacity-100 transition`} />
                    <div className="relative z-10">
                      <div className={`label-eyebrow mb-4 ${meta.accent}`}>{meta.eyebrow}</div>
                      <Avatar className="w-16 h-16 ring-2 ring-slate-200 mb-5">
                        <AvatarImage src={p.avatar} alt={p.name} />
                        <AvatarFallback className="bg-slate-200/60 text-slate-800">{p.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="font-display text-2xl font-semibold mb-1">{p.name}</div>
                      <div className="text-sm text-slate-600 mb-4">{p.title}</div>
                      <p className="text-sm text-slate-700 leading-relaxed mb-6 min-h-[3rem]">{p.description}</p>
                      <div className="flex items-center gap-2 text-sm text-sky-600 font-medium group-hover:gap-3 transition-all">
                        {p.label} <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* Sector-specific tenant modules (perakende vb.) */}
        {isSectorTenant && (
          <div data-testid="sector-modules">
            {/* Tenant personas */}
            {tenantPersonas.length > 0 && (
              <>
                <div className="mb-8">
                  <div className="label-eyebrow mb-3 flex items-center gap-2 flex-wrap">
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[0.65rem] font-medium border"
                      style={{
                        borderColor: `${tenant?.brand_color || "#0F172A"}30`,
                        color: tenant?.brand_color || "#0F172A",
                        background: `${tenant?.brand_color || "#0F172A"}08`,
                      }}
                    >
                      <Sparkles className="w-3 h-3" />
                      {tenant?.industry === "retail" ? "Perakende Edisyonu" : "Sektörel Edisyon"}
                    </span>
                    <span>DEMO PERSONAYI SEÇİN</span>
                  </div>
                  <div className="text-sm text-slate-500">
                    Aynı platform, üç farklı bakış. {tenant?.name} kütüphanesinden seçilmiş gerçek profiller.
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-14">
                  {tenantPersonas.map((p, idx) => {
                    const meta = ROLE_META[p.role] || ROLE_META.employee;
                    return (
                      <button
                        key={p.id}
                        data-testid={`tenant-persona-${p.role}`}
                        onClick={() => selectTenantPersona(p)}
                        style={{ animationDelay: `${idx * 80}ms` }}
                        className={`fade-up group text-left relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 transition-all hover:-translate-y-1 hover:shadow-lg ${meta.border}`}
                      >
                        <div className={`absolute inset-0 bg-gradient-to-br ${meta.color} opacity-100 transition`} />
                        <div className="relative z-10">
                          <div className={`label-eyebrow mb-4 ${meta.accent}`}>{meta.eyebrow}</div>
                          <div className="flex items-start justify-between mb-5 gap-2">
                            <Avatar className="w-16 h-16 ring-2 ring-slate-200">
                              <AvatarImage src={p.avatar} alt={p.name} />
                              <AvatarFallback className="bg-slate-200/60 text-slate-800">{p.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            {p.is_talent && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[0.6rem] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                <Star className="w-2.5 h-2.5 fill-amber-500" /> Yetenek
                              </span>
                            )}
                          </div>
                          <div className="font-display text-2xl font-semibold mb-1">{p.name}</div>
                          <div className="text-sm text-slate-600 mb-1">{p.title}</div>
                          <div className="text-xs text-slate-500 mb-4">
                            {p.branch_name} · Band {p.salary_band}
                            {p.performance_rating ? ` · Perf ${p.performance_rating.toFixed(1)}` : ""}
                          </div>
                          <p className="text-sm text-slate-700 leading-relaxed mb-6 min-h-[3rem]">{p.description}</p>
                          <div className="flex items-center gap-2 text-sm text-sky-600 font-medium group-hover:gap-3 transition-all">
                            {p.label} <ArrowRight className="w-4 h-4" />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            <div className="mb-8">
              <div className="label-eyebrow mb-3">SEKTÖREL VERİ MODÜLLERİ</div>
              <div className="text-sm text-slate-500">
                Persona seçmeden doğrudan sektörel kütüphane, kariyer yolları veya çalışan havuzuna gidin.
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  icon: BookOpen,
                  title: "Yetkinlik Kütüphanesi",
                  desc: "37 yetkinlik · 4 kategori · 5 seviyeli davranışsal çıpalar",
                  path: `/tenant/${tenant?.slug}/library`,
                  testid: "sector-card-library",
                  color: "#0ea5e9",
                },
                {
                  icon: GitBranch,
                  title: "Kariyer Yolları",
                  desc: "10 kariyer yolu · Band A → E · Yetkinlik & KPI mapping",
                  path: `/tenant/${tenant?.slug}/career-paths`,
                  testid: "sector-card-career",
                  color: "#8b5cf6",
                },
                {
                  icon: Users,
                  title: "Çalışan Havuzu",
                  desc: "300 aktif çalışan · 21 şube · departman & bölge filtreleri",
                  path: `/tenant/${tenant?.slug}/workforce`,
                  testid: "sector-card-workforce",
                  color: "#10b981",
                },
              ].map((it) => {
                const Icon = it.icon;
                return (
                  <Link
                    key={it.title}
                    to={it.path}
                    data-testid={it.testid}
                    className="group text-left relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 transition-all hover:-translate-y-1 hover:shadow-lg hover:border-slate-300"
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                      style={{ background: `${it.color}15`, color: it.color }}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="font-display text-xl font-semibold mb-2 text-slate-900">{it.title}</div>
                    <p className="text-sm text-slate-600 leading-relaxed mb-5 min-h-[2.5rem]">{it.desc}</p>
                    <div className="flex items-center gap-2 text-sm font-medium group-hover:gap-3 transition-all" style={{ color: it.color }}>
                      İncele <ArrowRight className="w-4 h-4" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        <p className="text-center text-xs text-slate-400 mt-16">
          Açıklanabilir AI · Türkçe · Kanıt tabanlı içgörü
        </p>
      </div>
      <GuidedTour scope="selector" autoStart={false} />
    </div>
  );
}
